'use client';

import { motion } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/shared/Button';
import { AnimatedCounter } from '@/components/shared/AnimatedCounter';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { addUTMs } from '@/lib/utils';
import type { HeroProContent, GlobalContent } from '@/lib/content-schema';

interface Props {
  content: HeroProContent;
  global: GlobalContent;
}

export function HeroPro({ content, global: g }: Props) {
  const posthog = usePostHog();
  const ctaHref = addUTMs(g.dashboardUrl + '/register', 'pro_hero');

  // Triple the marquee items for seamless loop
  const marquee = [...content.marqueeItems, ...content.marqueeItems, ...content.marqueeItems];

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-zibarit-gris-oscuro to-zibarit-verde-oscuro overflow-hidden">
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 flex flex-col items-center text-center"
      >
          <motion.p
            variants={fadeInUp}
            className="text-body text-lg text-zibarit-bone/70 mb-4"
          >
            {content.subtitle}
          </motion.p>

          <motion.h1
            variants={fadeInUp}
            className="text-title text-5xl md:text-7xl lg:text-8xl text-zibarit-bone mb-6"
          >
            {content.titleBefore}
            <span className="text-title-accent text-zibarit-verde-brillante">
              {content.titleAccent}
            </span>
            {content.titleAfter}
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-body text-base md:text-lg text-zibarit-bone/80 max-w-xl mb-10"
          >
            {content.description}
          </motion.p>

          {/* Stats */}
          <motion.div
            variants={fadeInUp}
            className="flex flex-wrap justify-center gap-8 mb-10"
          >
            {content.stats.map((stat) => (
              <AnimatedCounter key={stat.label} value={stat.value} suffix={stat.suffix} label={stat.label} className="text-center" />
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            {content.ctaPrimaryVisible && (
              <Button
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                magnetic
                size="lg"
                data-ph-capture
                onClick={() => posthog?.capture('landing_cta_clicked', {
                  button_text: content.ctaPrimaryText,
                  destination_url: ctaHref,
                  page: 'pro',
                })}
              >
                {content.ctaPrimaryText}
              </Button>
            )}
            {content.ctaSecondaryVisible && (
              <Button
                href={content.ctaSecondaryHref}
                variant="secondary"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-zibarit-negro"
                onClick={() => posthog?.capture('landing_cta_clicked', {
                  button_text: content.ctaSecondaryText,
                  destination_url: content.ctaSecondaryHref,
                  page: 'pro',
                })}
              >
                {content.ctaSecondaryText}
              </Button>
            )}
          </motion.div>
      </motion.div>

      {/* Event types marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute bottom-0 left-0 right-0 z-10 overflow-hidden border-t border-zibarit-bone/10"
      >
        <div className="flex animate-marquee w-max">
          {marquee.map((label, i) => (
            <span
              key={`${label}-${i}`}
              className="flex items-center gap-3 px-6 py-3.5 text-sm font-lato font-semibold uppercase tracking-widest text-zibarit-bone/50 whitespace-nowrap shrink-0"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-zibarit-verde-brillante/60 shrink-0" />
              {label}
            </span>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
